<template>
  <el-dialog :title="$t('signedDocument')" :visible="isShowDialog" @close="onClosePopupButtonClick()" width="700px;">
    <el-container>
      <el-main>
        <div class="filter-by-keyword">
          <el-row>
            <el-col :span="8">
              <el-input
                :placeholder="$t('keyword')"
                v-model="keyword"
                @input="isSearching = true">
              </el-input>
            </el-col>
            <el-col :span="2">
              <el-button size="small"
                         style="margin-left: 8px; margin-top: 4px;"
                         @click="onClearButtonClick()">
                {{$t('clear')}}
              </el-button>
            </el-col>
          </el-row>
        </div>
        <div v-loading="isLoading || isSearching">
          <div>
            <table class="table-signed-document" border="0" cellspacing="0" cellpadding="0">
              <tr v-for="(signedDocument, index) in signedDocumentPerPageList"
                  :key="`${index}`"
                  :class="{'lastest-file':(signedDocument.isLatest === true)}">
                <td width="33%">{{signedDocument.agentName}}</td>
                <td width="35%">
                  <div class="signed-document-default"
                       style="float: left; margin-top: 5px; margin-right: 5px;"></div>
                  <div style="float:left">{{getDocumentName(signedDocument.name)}}</div>
                </td>
                <td width="17%">{{getCreatedAt(signedDocument.createdAt)}}</td>
                <td width="15%" align="right">
                  <el-button type="primary"
                             size="small"
                             @click="onDownloadButtonClick(signedDocument)">
                    {{$t('download')}}
                  </el-button></td>
              </tr>
            </table>
          </div>
          <div>
            <el-pagination
              background
              layout="prev, pager, next"
              :total="signedDocumentList.length"
              :page-size="pageSize"
              :current-page="currentPage"
              @current-change = "changePage">
            </el-pagination>
          </div>
        </div>
        <div style="margin-top: 20px">
          <h4>{{$t('addSignedDocument')}}</h4>
          <div class="upload-container">
            <el-row>
              <el-col :span="6">
                <el-select v-model="selectAgentId"
                           filterable
                           size="small">
                  <el-option :label="$t('selectAgent')" value=""></el-option>
                  <el-option
                    v-for="(agent, index) in agentList"
                    :key="index"
                    :label="agent.name"
                    :value="agent.id"></el-option>
                </el-select>
              </el-col>
              <el-col :span="14">
                <input-file-component v-model="fileUploadData"
                                      :size="'small'"></input-file-component>
             </el-col>
              <el-col :span="4"
                      style="text-align: right">
                <el-button type="primary"
                           size="small"
                           icon="el-icon-upload"
                           :disabled="isDisabledUpload()"
                           @click="onUploadButtonClick()">{{$t('upload')}}</el-button>
              </el-col>
            </el-row>
          </div>
        </div>
      </el-main>
    </el-container>
  </el-dialog>
</template>

<script>
import i18nMessages from '@/translations/SpecialOffer/components/PopupSignedDocumentList.i18n'
import SpecialOfferApi from '@/api/special-offer-api'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import LoadingViewMixin from '@/mixins/loading-view-mixin'
import NotificationMixin from '@/mixins/notification-mixin'
import { get, cloneDeep, setWith, debounce } from 'lodash'
import { filterDataByKeyword, getDataOnPaging, adjustCurrentPage } from '@/services/general-service'
import { dateFormat } from '@/helpers/datetime'
import { forceDownloadFileUrl } from '@/helpers'
import InputFileComponent from '@/components/InputFileComponent'

export default {
  name: 'PopupSignedDocumentComponent',
  components: { InputFileComponent },
  i18n: { messages: i18nMessages },
  mixins: [AlertViewMixin, LoadingViewMixin, NotificationMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    specialOfferId: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      signedDocumentList: [],
      agentList: [],
      signedDocumentPerPageList: [],
      isShowDialog: false,
      currentPage: 1,
      pageSize: 5,
      isLoading: true,
      fileUploadData: {},
      selectAgentId: '',
      isSearching: false,
      keyword: ''
    }
  },
  watch: {
    visible: function (newValue) {
      this.isShowDialog = newValue
      if (newValue === true && this.specialOfferId !== '') {
        this.initData()
      }
    },
    keyword: debounce(function () {
      this.isSearching = false

      if (this.keyword === '') {
        this._prepareSignedDocumentPerPageList()
      }
    }, 300),
    isSearching: function (value) {
      if (!value) {
        this._prepareSignedDocumentPerPageList()
      }
    }
  },
  methods: {
    initData: async function () {
      this.isLoading = true
      this.signedDocumentList = []
      try {
        const result = await SpecialOfferApi.detail(this.specialOfferId)
        this.agentList = get(result.specialOfferDetail, 'agentList', [])

        this._groupAndSortSignedDocumentList()
        this._prepareSignedDocumentPerPageList()
      } catch (err) {
        this.alertError(err.message)
      }
      this.isLoading = false
    },
    onClosePopupButtonClick: function () {
      this._closePopup()
    },
    changePage: function (page) {
      this.currentPage = page
      this._prepareSignedDocumentPerPageList()
    },
    getCreatedAt: function (unixTime) {
      const createdAt = new Date(unixTime * 1000)

      return dateFormat.short(createdAt)
    },
    isDisabledUpload: function () {
      const fileName = get(this.fileUploadData, 'name', '')
      const content = get(this.fileUploadData, 'content', '')
      if (fileName === '' || content === '') {
        return true
      }

      return false
    },
    onDownloadButtonClick: async function (signedDocument) {
      this.startLoading()

      try {
        const data = {
          fileUrl: signedDocument.url
        }
        const documentResult = await SpecialOfferApi.downloadSignedDocument(data)
        const documentData = `data:${signedDocument.mimeType}; base64, ${documentResult}`
        forceDownloadFileUrl(documentData, signedDocument.name)
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    onUploadButtonClick: async function () {
      this.startLoading()
      try {
        const data = {
          agentId: this.selectAgentId,
          fileUpload: this.fileUploadData
        }
        let result = await SpecialOfferApi.uploadSignedDocument(this.specialOfferId, data)

        this.agentList = result.agentList

        this.agentList.forEach((agent) => {
          if (agent.id === this.selectAgentId) {
            const signedDocList = get(agent, 'signedDocumentList', [])
            setWith(agent, 'signedDocumentList', signedDocList, Object)
          }
        })
        this._groupAndSortSignedDocumentList()
        this._prepareSignedDocumentPerPageList()

        const title = this.$t('success')
        const message = this.$t('uploadSuccessMessage')
        this.showNotificationSuccess(title, message)

        this.$emit('onUploaded', this.specialOfferId)

        this.fileUploadData = {}
      } catch (err) {
        this.alertError(err.message)
      }
      this.stopLoading()
    },
    getDocumentName: function (name) {
      if (name.length > 20) {
        return `${name.substring(0, 16)}...`
      }

      return name
    },
    _filterSignedDocumentList: function () {
      const searchKeys = ['name', 'agentName']
      const filteredSignedDocumentList = filterDataByKeyword(this.signedDocumentList, this.keyword, searchKeys)

      return filteredSignedDocumentList
    },
    _prepareSignedDocumentPerPageList: function () {
      const filterSignedDocumentList = this._filterSignedDocumentList()
      this.currentPage = adjustCurrentPage(filterSignedDocumentList.length, this.currentPage, this.pageSize)
      const tempList = cloneDeep(filterSignedDocumentList)
      this.signedDocumentPerPageList = getDataOnPaging(tempList, this.currentPage, this.pageSize)
    },
    _closePopup: function () {
      this.isShowDialog = false
      this.currentPage = 1
      this.signedDocumentList = []
      this.signedDocumentPerPageList = []
      this.$emit('onClose')
    },
    _groupAndSortSignedDocumentList: function () {
      this.signedDocumentList = []
      let latestOfEachAgentList = []
      this.agentList.forEach((agent) => {
        const agentObj = { agentId: agent.id, agentName: agent.name }
        let signedDocList = cloneDeep(get(agent, 'signedDocumentList', []))

        if (signedDocList.length > 0) {
          let latest = signedDocList.shift()
          latest = Object.assign({}, latest, agentObj, { isLatest: true })
          latestOfEachAgentList.push(latest)
        }

        signedDocList.forEach((signedDocument) => {
          signedDocument = Object.assign({}, signedDocument, agentObj)
          this.signedDocumentList.push(signedDocument)
        })
      })

      latestOfEachAgentList = this._sortByCreatedAt(latestOfEachAgentList)
      this.signedDocumentList = this._sortByCreatedAt(this.signedDocumentList)
      this.signedDocumentList.unshift(...latestOfEachAgentList)
    },
    _sortByCreatedAt: function (singedDocumentList) {
      const result = singedDocumentList.sort((a, b) => {
        return (a.createdAt > b.createdAt) ? -1 : 1
      })

      return result
    },
    onClearButtonClick: function () {
      this.keyword = ''
    }
  }
}
</script>
<style scoped>
  .table-signed-document {
    width: 100%;
    margin-bottom: 22px;
  }

  .table-signed-document td {
    padding: 10px;
    border-bottom: 1px #ddd solid;
    font-size: 14px;
  }
  .upload-container {
    border: solid 1px #409EFF;
    border-radius: 15px;
    padding: 20px;
  }
  .lastest-file {
    font-weight: 600;
  }
  div.btn-upload {
    overflow: hidden;
    text-align: center;
    position: relative;
    margin: 0px;
    transition: all ease 0.2s;
    width: 100%;
    text-align: left;
    padding-left: 15px;
  }

  input.upload {
    bottom: 0px;
    right: 0px;
    opacity: 0;
    width: 100%;
    height: 100%;
    outline: none;
    cursor: pointer;
    position: absolute;
    z-index: 100;
  }
  .filter-by-keyword {
    padding-bottom: 10px;
    border-bottom: 1px #000 solid;
  }
</style>
